import type { MyFilesBreadcrumb } from "@nlc/shared-types";

import {
  formatAllowsMultipleDesigns,
  MAX_DESIGNS_PER_POST,
} from "@nlc/shared-types";
import { ChevronRight } from "lucide-react";
import { Fragment, useState } from "react";

import type { MyFilesSearchSchema } from "@/features/my-files/page-search-schema";
import type { FileItem, View } from "@/features/my-files/types";

import { Button } from "@/components/button";
import { Loader } from "@/components/Loader";
import { Tab, TabList, TabPanel, Tabs } from "@/components/tabs";
import { ToggleViewButtons } from "@/features/my-files/components/toggle-view-buttons";
import { useGetMyFilesStructure } from "@/features/my-files/hooks/useGetMyFilesStructure";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId";

import type { PostType } from "../post-type-step/postTypes";
import type { UploadsChange } from "./DesignUploader";
import type { DesignFile } from "./types";

import { AnimatedSidePanel } from "../../AnimatedSidePanel";
import { DesignPreview } from "./DesignPreview";
import { DesignsGridView } from "./DesignsGridView";
import { DesignsListView } from "./DesignsListView";
import { DesignUploader } from "./DesignUploader";

const DESIGN_SOURCE_PANEL = "p-0 outline-hidden data-[inert]:hidden";

export function DesignStep({
  brandId,
  onSelectDesigns,
  postType,
  selectedDesigns,
}: {
  brandId: number;
  onSelectDesigns: (designs: DesignFile[]) => void;
  postType: PostType;
  selectedDesigns: DesignFile[];
}) {
  const allowsMultiple = formatAllowsMultipleDesigns(postType.format);
  const selectedIds = selectedDesigns.map((design) => design.id);
  const projectId = useGetCurrentProjectId();
  const [view, setView] = useState<View>("grid");
  const [location, setLocation] = useState<MyFilesSearchSchema>({ brandId });
  const { data, isFetching } = useGetMyFilesStructure({
    ...location,
    projectId,
  });

  const files = (data?.files ?? []).filter(
    (fileItem) => fileItem.type === "file" || fileItem.href !== "trash",
  );
  const breadcrumbs = data?.breadcrumbs ?? [];
  const brandCrumbIndex = breadcrumbs.findIndex(
    (crumb) => crumb.slug === "brand",
  );

  function open(fileItem: FileItem) {
    if (fileItem.type === "file") {
      toggleDesign(fileItem);

      return;
    }

    setLocation((current) => ({
      ...current,
      ...locationOfFolder(fileItem.href, fileItem.id),
    }));
  }

  function toggleDesign(design: DesignFile) {
    if (selectedIds.includes(design.id)) {
      onSelectDesigns(selectedDesigns.filter(({ id }) => id !== design.id));
    } else {
      onSelectDesigns(allowsMultiple ? [...selectedDesigns, design] : [design]);
    }
  }

  function applyUploads({ added, removedIds }: UploadsChange) {
    const kept = selectedDesigns.filter(
      ({ id }) => !removedIds.includes(id as number),
    );
    const latestUpload = added.at(-1);

    if (allowsMultiple) {
      onSelectDesigns([...kept, ...added]);
    } else {
      onSelectDesigns(latestUpload ? [latestUpload] : kept);
    }
  }

  function goToBreadcrumb(index: number) {
    setLocation(locationOfBreadcrumbs(breadcrumbs.slice(0, index + 1)));
  }

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-xs">
        <h2 className="text-lg font-semibold text-text-primary">
          {allowsMultiple
            ? "Which designs do you want to post?"
            : "Which design do you want to post?"}
        </h2>
        <p className="text-sm text-text-tertiary">
          {allowsMultiple
            ? `Pick from the brand files or upload new ones. Pick up to ${MAX_DESIGNS_PER_POST} images to post a carousel, in the order they should appear.`
            : "Pick the design to publish from the brand files, or upload a new one."}
        </p>
      </div>

      <div className="flex items-start">
        <Tabs className="min-w-0 flex-1 gap-lg bg-transparent">
          <TabList aria-label="Design source" className="bg-transparent">
            <Tab id="my-files">My files</Tab>
            <Tab id="upload">Upload</Tab>
          </TabList>

          {/* Force-mounted so uploads in progress survive switching tabs. */}
          <TabPanel
            className={DESIGN_SOURCE_PANEL}
            id="my-files"
            shouldForceMount
          >
            <div className="flex flex-col divide-y divide-border-secondary rounded-xl border border-border-secondary bg-bg-primary">
              <div className="flex items-center justify-between gap-lg px-xl py-lg">
                <nav aria-label="Folder" className="min-w-0">
                  <ol className="flex items-center gap-1">
                    {breadcrumbs.slice(brandCrumbIndex).map((crumb, offset) => {
                      const index = brandCrumbIndex + offset;
                      const isCurrent = index === breadcrumbs.length - 1;

                      return (
                        <Fragment key={crumb.slug + crumb.id}>
                          {offset > 0 && (
                            <li aria-hidden>
                              <ChevronRight className="size-4 text-fg-quaternary" />
                            </li>
                          )}
                          <li className="min-w-0">
                            {isCurrent ? (
                              <span
                                aria-current="location"
                                className="block max-w-[200px] truncate rounded-sm bg-breadcrumb-bg-hover px-md py-xs text-sm font-semibold text-breadcrumb-fg"
                              >
                                {crumb.label}
                              </span>
                            ) : (
                              <Button
                                className="max-w-[200px] truncate"
                                color="gray"
                                onPress={() => goToBreadcrumb(index)}
                                size="sm"
                                variant="tertiary"
                              >
                                {crumb.label}
                              </Button>
                            )}
                          </li>
                        </Fragment>
                      );
                    })}
                  </ol>
                </nav>
                <ToggleViewButtons
                  setGrouping={() => {}}
                  setView={setView}
                  view={view}
                />
              </div>

              <div className="max-h-[560px] overflow-y-auto px-xl py-lg">
                {isFetching ? (
                  <Loader className="py-20" />
                ) : files.length === 0 ? (
                  <p className="py-20 text-center text-sm text-text-secondary">
                    This folder is empty.
                  </p>
                ) : view === "list" ? (
                  <DesignsListView
                    files={files}
                    onOpen={open}
                    selectedFileIds={selectedIds}
                  />
                ) : (
                  <DesignsGridView
                    files={files}
                    onOpen={open}
                    selectedFileIds={selectedIds}
                  />
                )}
              </div>
            </div>
          </TabPanel>

          <TabPanel
            className={DESIGN_SOURCE_PANEL}
            id="upload"
            shouldForceMount
          >
            <DesignUploader
              brandId={brandId}
              onUploadsChange={applyUploads}
              postType={postType}
            />
          </TabPanel>
        </Tabs>

        <AnimatedSidePanel
          contentKey={
            selectedDesigns.length === 1
              ? `design-${selectedDesigns[0]?.id}`
              : "designs"
          }
          isOpen={selectedDesigns.length > 0}
        >
          {selectedDesigns.length > 0 && (
            <DesignPreview
              brandId={brandId}
              designs={selectedDesigns}
              onClose={() => onSelectDesigns([])}
              postType={postType}
            />
          )}
        </AnimatedSidePanel>
      </div>
    </div>
  );
}

function locationOfFolder(
  href: string,
  id: FileItem["id"],
): MyFilesSearchSchema {
  if (href === "sourcefiles" || href === "archive" || href === "trash") {
    return { folder: href };
  }
  if (href === "requestType") {
    return { requestType: id as MyFilesSearchSchema["requestType"] };
  }

  return { [`${href}Id`]: id };
}

function locationOfBreadcrumbs(
  breadcrumbs: MyFilesBreadcrumb[],
): MyFilesSearchSchema {
  const location: MyFilesSearchSchema = {};

  for (const crumb of breadcrumbs) {
    if (crumb.slug === "brand") location.brandId = crumb.id as number;
    if (crumb.slug === "requestType") {
      location.requestType = crumb.id as MyFilesSearchSchema["requestType"];
    }
    if (crumb.slug === "request") location.requestId = crumb.id as number;
    if (crumb.slug === "folder") {
      location.folder = crumb.id as MyFilesSearchSchema["folder"];
    }
  }

  return location;
}
